import React, { useState } from "react";
import { Vote, FlaskConical, Lock, LayoutGrid, Radio, UserCheck, Landmark, ClipboardList, MonitorPlay, HelpCircle, Scale, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/context/AuthContext";
import { useElectionDay } from "@/context/ElectionDayContext";
import { isGeneralManager } from "@/lib/roles";
import CommitteePicker from "@/components/election/CommitteePicker";
import CommitteeBallot from "@/components/election/CommitteeBallot";
import TrackingCenter from "@/components/election/TrackingCenter";
import CommitteeResults from "@/components/election/CommitteeResults";
import OfficerAssign from "@/components/election/OfficerAssign";
import RehearsalReport from "@/components/election/RehearsalReport";
import RehearsalPanel from "@/components/election/RehearsalPanel";
import ReconcileReport from "@/components/election/ReconcileReport";
import CouncilTally from "@/components/election/CouncilTally";

export default function ElectionDay() {
  const { ed } = useElectionDay();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("sandik");
  const [cno, setCno] = useState(null);

  const gm = isGeneralManager(user);

  const tabs = [
    ["sandik", "Komite Sandık", LayoutGrid],
    ["takip", "Takip Merkezi", Radio],
    ["sonuclar", "Seçim Sonuçları", Landmark],
    ...(gm ? [["meclis", "Meclis Çetelesi (120 Üye)", Users], ["muhakeme", "Muhakeme / Tutarlılık", Scale], ["gorevli", "Sandık Görevlileri", UserCheck], ["karne", "Prova Karnesi", ClipboardList]] : []),
  ];

  const openCommittee = (n) => { setCno(n); setTab("sandik"); window.scrollTo({ top: 0 }); };

  if (ed && !ed.tally_open) {
    return (
      <Card className="p-8 text-center border border-dashed border-border" data-testid="ed-closed">
        <Lock className="h-7 w-7 mx-auto text-muted-foreground" />
        <h1 className="font-display text-xl font-bold mt-3">Seçim Çetelesi henüz açılmadı</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Açılış: {String(ed.tally_open_at || "").slice(0, 16).replace("T", " ")}
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-5" data-testid="election-day-page">
      <div className="flex flex-wrap items-start gap-3">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[#b3122b]/10 text-[#b3122b]">
          <Vote className="h-6 w-6" />
        </span>
        <div className="min-w-0">
          <h1 className="font-display text-2xl sm:text-3xl font-bold">Seçim Çetelesi</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            11 Ekim 2026 · Komite sandık girişi, Takip Merkezi ve araç operasyonu
          </p>
        </div>
        <button type="button" onClick={() => navigate("/cetele-kilavuzu")}
          data-testid="open-ballot-guide"
          className="inline-flex items-center gap-2 rounded-full border-2 border-[#0F2C59] bg-card px-4 py-2.5 text-sm font-bold text-[#0F2C59] transition-transform hover:scale-[1.03] dark:border-sky-400 dark:text-sky-300">
          <HelpCircle className="h-4 w-4" /> NASIL YAPILIR?
        </button>
        {ed?.can_scoreboard && (
          <button type="button" onClick={() => navigate("/sonuc-ekrani")}
            data-testid="open-scoreboard"
            className="inline-flex items-center gap-2 rounded-full bg-[#0F2C59] px-4 py-2.5 text-sm font-bold text-white transition-transform hover:scale-[1.03]">
            <MonitorPlay className="h-4 w-4" /> SONUÇ EKRANI
          </button>
        )}
        {ed?.tally_test && (
          <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold text-white"
            style={{ background: "#3f2f6e" }} data-testid="ed-test-badge">
            <FlaskConical className="h-3.5 w-3.5" /> TEST MODU
          </span>
        )}
      </div>

      <div className="flex flex-wrap gap-2 border-b border-border pb-2" data-testid="ed-tabs">
        {tabs.map(([k, label, Icon]) => (
          <button key={k} type="button" onClick={() => setTab(k)}
            data-testid={`ed-tab-${k}`}
            className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-semibold transition-colors ${
              tab === k ? "bg-[#0F2C59] text-white" : "bg-muted text-muted-foreground hover:bg-muted/70"}`}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {gm && ed?.tally_test && <RehearsalPanel />}

      {tab === "sandik" && (cno
        ? <CommitteeBallot committeeNo={cno} onBack={() => setCno(null)} />
        : <CommitteePicker onPick={setCno} />)}
      {tab === "takip" && <TrackingCenter onOpenCommittee={openCommittee} />}
      {tab === "sonuclar" && <CommitteeResults />}
      {tab === "meclis" && gm && <CouncilTally />}
      {tab === "muhakeme" && gm && <ReconcileReport />}
      {tab === "gorevli" && gm && <OfficerAssign />}
      {tab === "karne" && gm && <RehearsalReport />}
    </div>
  );
}
